/**
 * Clock health for offline attendance (implementation plan FE-5, D10).
 *
 * An offline punch is stamped on the device, so its time is only as good as
 * the device clock. This module owns the three parts of trusting it:
 *
 *  - CALIBRATION ({@link calibrate}, {@link correctedNow}) — every online
 *    provisioning measures the offset between the device's wall clock and
 *    ERPat's server time from the request round trip: at the request's
 *    wall-clock MIDPOINT, with an uncertainty that grows with any gap between
 *    the wall-clock and the monotonic round trip (a device suspended, or a date
 *    changed, while the request was out). A capture is stamped
 *    `device now + offset` (the corrected time), with the raw device time and
 *    the calibration travelling beside it for diagnostics.
 *
 *  - JUMP DETECTION ({@link ClockMonitor}) — a calibration is only valid while
 *    the wall clock keeps the time it had when it was measured. The monitor
 *    compares the wall clock (`Date.now`) with the monotonic clock
 *    (`performance.now`, which no date change can move) over each interval
 *    between observations:
 *      · the wall clock moved BACK relative to the monotonic one by more than
 *        the policy threshold ⇒ unsafe (`jump_back`); nothing innocent does that;
 *      · it moved FORWARD by more than the threshold ⇒ unsafe (`jump_forward`)
 *        ONLY when the page was hidden at no point of the interval — a sleeping
 *        device pauses the monotonic clock while the wall clock keeps time, and
 *        sleep always passes through `visibilitychange: hidden`, so a forward
 *        difference across a hidden period is accepted (D10). Whether the page
 *        is hidden is told by `markHidden()` / `markVisible()` and re-read from
 *        a visibility probe at every observation, so a missed event can only
 *        err toward unsafe (see the class note);
 *      · the wall clock fell more than the threshold below the HIGHEST wall
 *        time seen since the last calibration ⇒ unsafe (`jump_back`) — this
 *        also catches a regression made in steps that are each under the
 *        threshold, and, persisted for the next start, one made while the app
 *        was closed (`regressed_since_last_run`, see `restore()`);
 *      · the calibration itself is less certain than the policy allows, or one
 *        ERPat would refuse to receive (an offset beyond ±365 days, an
 *        unreadable server time) ⇒ unsafe (`uncertain`).
 *    A jump verdict is STICKY: only a fresh online calibration clears it. A
 *    recalibration never rewrites a timestamp already stamped — it only
 *    changes how the NEXT capture is stamped.
 *
 *  - STAMPING ({@link ClockMonitor.stamp}) — the ONE source of a capture's
 *    times (plan amendment C10): it observes the clocks like `check()` and
 *    hands out the verdict, the HELD calibration, the device time and the
 *    corrected time together. A calibration is a fact about the device clock
 *    measured against one ERPat origin, not about an account: the monitor holds
 *    the freshest one, its verdict covers it, and it supersedes the calibration
 *    stored with any account's context. A precise calibration measured in this
 *    run is not replaced by a newer one too imprecise to stamp with when the
 *    two agree and the clock has not jumped since (`calibrated()`).
 *
 * Pure: no storage, no network, no Vue. The time sources and the visibility
 * probe are injected (the defaults read `Date.now`, `performance.now` and
 * `document.visibilityState`); the orchestrator persists `lastSeenWallMs()` and
 * `calibration()` per ERPat origin and feeds visibility changes in.
 */
import { isIsoUtc } from '@/contracts/offline-attendance-v1';
import {
  OFFLINE_CLOCK_JUMP_DEFAULT_MS,
  OFFLINE_CLOCK_UNCERTAINTY_DEFAULT_MS,
} from '@/contracts/offline-attendance-policy';
import type { ClockCalibration } from '@/types/offline-attendance';

/**
 * ERPat's limit on an event's `clock.offset_ms`: ±365 days. Beyond it ERPat
 * FINAL-rejects the event as `invalid_event` (`attendance_offline_normalize_event()`),
 * so a device clock that far off — a dead RTC, a manual date — would have every
 * punch it stamps refused permanently. Such a calibration reads `uncertain`.
 */
export const OFFLINE_CLOCK_MAX_OFFSET_MS = 31_536_000_000;

/* ── Calibration ───────────────────────────────────────────────────────── */

/**
 * Calibrate the device clock against ERPat's `server_time` from one request.
 *
 * ERPat read its clock somewhere inside the round trip. Assuming the middle of
 * it on the WALL clock — the clock every capture reads —
 *
 *   wallRtt     = receivedDeviceMs − sentDeviceMs
 *   monoRtt     = receivedPerfMs − sentPerfMs   (when finite and ≥ 0, else max(0, wallRtt))
 *   offset      = serverMs − (sentDeviceMs + wallRtt/2)        (rounded to a whole ms)
 *   uncertainty = ceil((monoRtt + |wallRtt − monoRtt|) / 2)
 *
 * In the normal case both clocks agree and this is the plan's
 * `serverMs − (sentDeviceMs + rtt/2)` ± `rtt/2`. When they disagree, the
 * device cannot tell WHERE in the round trip the wall clock moved, and no
 * single-ended anchor survives every case:
 *   · the device suspends after ERPat answered but before the continuation
 *     reads `Date.now()` (the phone locked right after Syntry opened): the
 *     receive reading jumps by the suspension, the monotonic clock does not;
 *   · it suspends after the send time was read but before the request left:
 *     the same, on the send side;
 *   · the date is changed while the request is out.
 * For one such disturbance the midpoint's error — against the offset the
 * device had before it AND the one it has after — stays within
 * `(monoRtt + |wallRtt − monoRtt|) / 2`, which is exactly the uncertainty. A
 * large disturbance therefore reads `uncertain` (capture blocked until a better
 * round trip) instead of silently stamping every punch hours off — unless the
 * monitor already holds a precise calibration from earlier in the run that this
 * one agrees with, which it then keeps ({@link ClockMonitor.calibrated}). Each
 * case is pinned in `src/test/offline/clock-health.spec.ts`.
 *
 * `calibratedAtDeviceMs` is that midpoint in whole milliseconds — the device
 * instant that corresponds to `calibratedAtServerIso`, so
 * `correctedNow(cal, cal.calibratedAtDeviceMs)` equals the server time exactly.
 *
 * @param serverIso ERPat's `server_time` — strict ISO UTC; it becomes the
 *   event's `clock.calibrated_at`, which ERPat validates.
 * @param sentPerfMs `performance.now()` read with `sentDeviceMs`.
 * @param receivedPerfMs `performance.now()` read with `receivedDeviceMs`. A
 *   non-finite difference (no monotonic clock) or a negative one (a broken
 *   clock) makes the wall round trip stand in for the monotonic one.
 * @throws RangeError for an invalid server time or a non-finite device time —
 *   a NaN calibration would silently stamp NaN punch times.
 */
export function calibrate(
  serverIso: string,
  sentDeviceMs: number,
  receivedDeviceMs: number,
  sentPerfMs: number,
  receivedPerfMs: number,
): ClockCalibration {
  if (!isIsoUtc(serverIso)) {
    throw new RangeError('calibrate: the server time is not an ISO-8601 UTC timestamp');
  }
  if (!Number.isFinite(sentDeviceMs) || !Number.isFinite(receivedDeviceMs)) {
    throw new RangeError('calibrate: the device send/receive times must be finite');
  }
  const serverMs = Date.parse(serverIso);
  const wallRtt = receivedDeviceMs - sentDeviceMs;
  const perfRtt = receivedPerfMs - sentPerfMs;
  // A monotonic round trip that is not a number (no monotonic clock) or ran
  // backwards (a broken one) measures nothing: the wall clock is all there is.
  const monoRtt = Number.isFinite(perfRtt) && perfRtt >= 0 ? perfRtt : Math.max(0, wallRtt);
  // `|| 0` turns a -0 into 0.
  const offsetMs = Math.round(serverMs - (sentDeviceMs + wallRtt / 2)) || 0;
  return {
    offsetMs,
    uncertaintyMs: Math.ceil((monoRtt + Math.abs(wallRtt - monoRtt)) / 2),
    calibratedAtDeviceMs: serverMs - offsetMs,
    calibratedAtServerIso: serverIso,
  };
}

/**
 * The corrected (server-aligned) time for a device wall-clock reading, in whole
 * milliseconds — what a capture's `occurred_at` is stamped with.
 *
 * Captures do not call this directly: {@link ClockMonitor.stamp} applies it to
 * the calibration the monitor HOLDS — never to a session's own stored
 * calibration, which may predate a clock change another account's provisioning
 * has measured, or be an imprecise measurement the monitor did not adopt.
 *
 * Pure: it never reads the monitor. Whether the device clock can be trusted
 * right now is {@link ClockMonitor}'s verdict, and capture is refused while it
 * is unsafe.
 */
export function correctedNow(cal: ClockCalibration, deviceNowMs: number = Date.now()): number {
  return Math.round(deviceNowMs + cal.offsetMs);
}

/** Whether this environment has the monotonic clock in-session jump detection relies on. */
export function monotonicClockAvailable(): boolean {
  return typeof performance !== 'undefined' && typeof performance.now === 'function';
}

/* ── Verdicts ──────────────────────────────────────────────────────────── */

export type ClockVerdict =
  | { status: 'safe' }
  | { status: 'unsafe'; reason: 'jump_back' | 'jump_forward' | 'uncertain' | 'regressed_since_last_run' };

type UnsafeReason = Extract<ClockVerdict, { status: 'unsafe' }>['reason'];

/** A sticky jump verdict: everything but `uncertain`, which follows the current calibration. */
type JumpReason = Exclude<UnsafeReason, 'uncertain'>;

export interface ClockMonitorOptions {
  /** Policy `clock_jump_block_s × 1000`; invalid ⇒ `OFFLINE_CLOCK_JUMP_DEFAULT_MS`. */
  jumpThresholdMs: number;
  /** Policy `clock_uncertainty_block_s × 1000`; invalid ⇒ `OFFLINE_CLOCK_UNCERTAINTY_DEFAULT_MS`. */
  uncertaintyThresholdMs: number;
  /** Wall clock (default `Date.now`). */
  now?: () => number;
  /** Monotonic clock (default `performance.now`; the wall clock when there is none). */
  perfNow?: () => number;
  /**
   * Whether the page is hidden RIGHT NOW (default: `document.visibilityState
   * === 'hidden'`; false without a document). Read at every observation to
   * start the next interval, so a missed `markVisible()` corrects itself. Only
   * an answer of exactly `true` counts as hidden; a probe that throws counts
   * as visible — both fail closed.
   */
  isHidden?: () => boolean;
  /**
   * Count the FIRST interval as hidden whatever the probe says, as if
   * `markHidden()` had run at construction (default: the probe decides).
   */
  hidden?: boolean;
}

/**
 * What a capture is stamped with — {@link ClockMonitor.stamp}, the one source
 * of a capture's times (plan amendment C10). FE-8 freezes the event from it:
 * `occurred_at = toIsoMs(correctedNowMs)`, `device_occurred_at =
 * toIsoMs(deviceNowMs)` and the `clock` block from `calibration`
 * (`offset_ms: Math.round(offsetMs)`, `calibrated_at: calibratedAtServerIso`,
 * `uncertainty_ms: Math.ceil(uncertaintyMs)` — C1). Readiness reads `verdict`
 * as its `clock` input and `correctedNowMs` as its `nowMs`.
 */
export interface ClockStamp {
  /** The verdict after observing the clocks for this stamp — refuse the capture when unsafe. */
  verdict: ClockVerdict;
  /** A copy of the calibration the monitor HOLDS (never a session's stored one). */
  calibration: ClockCalibration;
  /** The device wall-clock time stamped, in whole milliseconds. */
  deviceNowMs: number;
  /**
   * The corrected (server-aligned) time: `deviceNowMs +
   * Math.round(calibration.offsetMs)` — what `correctedNow(calibration,
   * deviceNowMs)` gives — so it differs from `deviceNowMs` by exactly the
   * `clock.offset_ms` the event carries.
   */
  correctedNowMs: number;
}

/** A threshold that is a finite, positive number of milliseconds — else the fallback. */
function threshold(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
}

/** The default visibility probe: the page reports itself hidden. */
function documentHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

function defaultPerfNow(): () => number {
  // Without a monotonic clock every interval compares the wall clock with
  // itself, so in-session jumps go unseen; the high-water mark and the
  // restart check still apply. `monotonicClockAvailable()` lets the
  // orchestrator treat such an environment as unsupported instead.
  return monotonicClockAvailable() ? () => performance.now() : () => Date.now();
}

/** The server time a calibration was measured at, or null when it cannot be read. */
function serverTimeOf(cal: ClockCalibration): number | null {
  return isIsoUtc(cal.calibratedAtServerIso) ? Date.parse(cal.calibratedAtServerIso) : null;
}

/**
 * Whether two measurements of the device clock can both be right: their
 * offsets differ by no more than their uncertainties together. An unreadable
 * offset or uncertainty agrees with nothing.
 *
 * Used to keep a precise calibration when a newer one is too imprecise to
 * stamp with: an imprecise measurement that agrees with the precise one is no
 * evidence the clock moved, while one that does not is.
 */
function agrees(a: ClockCalibration, b: ClockCalibration): boolean {
  const gap = Math.abs(a.offsetMs - b.offsetMs);
  const room = a.uncertaintyMs + b.uncertaintyMs;
  return (
    Number.isFinite(gap) &&
    Number.isFinite(room) &&
    a.uncertaintyMs >= 0 &&
    b.uncertaintyMs >= 0 &&
    gap <= room
  );
}

/**
 * Watches the device clock between calibrations (D10) and holds the
 * calibration every capture is stamped with.
 *
 * The orchestrator's duties (plan amendments C10–C12):
 *   - ONE instance per JS process — a module-level singleton that outlives
 *     sign-out and sign-in, because the device clock does not change with the
 *     account — created with the stored policy's thresholds
 *     (`clock_jump_block_s` / `clock_uncertainty_block_s` × 1000, else the
 *     `OFFLINE_CLOCK_*_DEFAULT_MS` defaults) and the default `isHidden` probe
 *     (`document.visibilityState === 'hidden'`);
 *   - `markHidden()` on visibility hidden, `pagehide` and the native pause,
 *     `markVisible()` on visible and the native resume — for the monitor's
 *     WHOLE life, not only while a session runs. The probe is the backstop:
 *     every observation re-reads it to start the next interval, so a missed
 *     `markVisible()` corrects itself at the next observation, and a missed
 *     `markHidden()` can only make a sleep read as a forward jump (blocked
 *     until the next online calibration — the safe way to be wrong). On the
 *     native shell, a probe that also reports hidden while the app is paused
 *     (the WebView may keep saying `visible`) avoids that false alarm;
 *   - at every session start (boot, or a later sign-in in the same run),
 *     `restore(lastSeen, calibration)` with the state persisted for the
 *     scope's `apiOrigin`, and again with the session's stored context
 *     calibration — `restore()` keeps the right one whatever the order;
 *   - right after every successful ONLINE provisioning, with no unrelated
 *     await in between: `setThresholds()` from the new policy, THEN
 *     `calibrated(record.calibration)`;
 *   - `stamp()` immediately before every capture — the ONLY source of the
 *     capture's times: `occurred_at` from `correctedNowMs`,
 *     `device_occurred_at` from `deviceNowMs`, the `clock` block from
 *     `calibration`; readiness takes `verdict` as its clock input and
 *     `correctedNowMs` as its `nowMs` (NaN when `stamp()` is null). Never
 *     stamp with a session's stored `context.calibration`;
 *   - `check()` on its 60 s visible timer;
 *   - persists `lastSeenWallMs()` and `calibration()` per ERPat origin
 *     (IndexedDB `meta` `clock_state:<apiOrigin>` — never one value for the
 *     whole device: a calibration measured against one ERPat must never stamp
 *     punches for another, nor send that server's `calibrated_at`) on that
 *     timer and after `calibrated()`;
 *   - while the verdict is unsafe for ANY reason and the device is online,
 *     re-provisions promptly, with backoff — `calibrated()` is the only cure,
 *     and until then capture is refused (`blocked/clock_unsafe`, no legacy
 *     POST fallback), online as well.
 *
 * Why stamping goes through the monitor: on a shared device the clock can be
 * set back while the app is closed. Account A re-provisions online and
 * `calibrated()` absorbs the change — it clears the jump verdicts and resets
 * the high-water mark. Account B then starts a session that cannot provision
 * (offline, or the context request fails). B's stored calibration predates
 * the change; stamping with it would put every punch off by the change while
 * the verdict reads safe (and `restore()` cannot see it: A's calibration
 * already reset the persisted last-seen time). `stamp()` uses A's fresh
 * calibration instead, which the verdict covers. Equally, after `calibrated()`
 * KEPT a precise calibration over an imprecise one, the stored context holds
 * the imprecise one, minutes off. Before any online calibration in a run,
 * `restore()` keeps the calibration ERPat vouched for MOST RECENTLY (by its
 * server time) among those it is given, whatever order sessions start in —
 * except that a stampable one beats an unstampable one it agrees with. Across
 * a restart that knowledge survives only if the orchestrator persists
 * `calibration()` for the origin and passes it to `restore()` alongside the
 * session's own.
 *
 * Observations: `check`, `stamp`, `markHidden`, `markVisible` and `restore`
 * JUDGE the interval since the previous observation, then anchor the next one
 * at `(now(), perfNow())`; `calibrated` anchors without judging (ERPat just
 * vouched for the time) — except when it keeps the held calibration, where it
 * judges first (see `calibrated()`). Every anchor re-reads the visibility
 * probe; `markHidden()` / `markVisible()` then override it for the interval
 * they open.
 */
export class ClockMonitor {
  private readonly now: () => number;
  private readonly perfNow: () => number;
  private readonly isHidden: () => boolean;
  private jumpThresholdMs: number;
  private uncertaintyThresholdMs: number;
  private anchorWallMs: number;
  private anchorPerfMs: number;
  /**
   * The page was hidden at some point since the current anchor: the probe said
   * so when the interval started, or `markHidden()` opened it. Never carried
   * over from an earlier interval — a missed `markVisible()` must not excuse
   * every later forward jump.
   */
  private hiddenSinceAnchor: boolean;
  /** The highest wall time seen since the last calibration (persisted for the next start). */
  private highWaterMs: number;
  /** The calibration captures are stamped with (a private copy). */
  private current: ClockCalibration | null = null;
  private sticky: JumpReason | null = null;
  /** `calibrated()` ran in this process: the previous run's records no longer apply. */
  private calibratedThisRun = false;

  constructor(opts: ClockMonitorOptions) {
    this.now = opts.now ?? (() => Date.now());
    this.perfNow = opts.perfNow ?? defaultPerfNow();
    this.isHidden = opts.isHidden ?? documentHidden;
    this.jumpThresholdMs = threshold(opts.jumpThresholdMs, OFFLINE_CLOCK_JUMP_DEFAULT_MS);
    this.uncertaintyThresholdMs = threshold(opts.uncertaintyThresholdMs, OFFLINE_CLOCK_UNCERTAINTY_DEFAULT_MS);
    this.anchorWallMs = this.now();
    this.anchorPerfMs = this.perfNow();
    this.highWaterMs = this.anchorWallMs;
    this.hiddenSinceAnchor = opts.hidden === true || this.probeHidden();
  }

  /**
   * A fresh calibration from an ONLINE provisioning: ERPat just vouched for
   * the time, so every jump verdict is cleared, the high-water mark restarts
   * at the current wall time, the next interval is anchored here, and this is
   * the calibration captures are stamped with from now on. (A new calibration
   * that is itself too uncertain still reads `uncertain`.)
   *
   * One exception keeps a good calibration: when `cal` cannot be stamped with
   * (typically `uncertain` — the phone slept while the provisioning answer was
   * outstanding, see {@link calibrate}) but the calibration held now CAN, was
   * measured earlier in THIS run (so the monitor has watched the clock ever
   * since), the interval since the last observation shows no jump, and the
   * two measurements agree (their offsets are within their uncertainties) —
   * then the held calibration stays: for stamping, for the verdict, and the
   * jump detection carries on from it. An imprecise answer that agrees with a
   * precise one is no evidence that the clock moved, and replacing the precise
   * one would block capture (offline too) until the next round trip. Anything
   * else — a jump, a disagreement, a held calibration restored from storage
   * rather than measured in this run — adopts `cal` as above.
   */
  calibrated(cal: ClockCalibration): void {
    const held = this.current;
    if (this.calibratedThisRun && held !== null && !this.stampable(cal) && this.stampable(held)) {
      // Judge the interval since the last observation first (this re-anchors).
      this.observe();
      if (this.sticky === null && agrees(cal, held)) return;
    }
    this.current = { ...cal };
    this.calibratedThisRun = true;
    this.sticky = null;
    this.reanchor(this.now(), this.perfNow());
    this.highWaterMs = this.anchorWallMs;
  }

  /**
   * The calibration the monitor holds: the one `calibrated()` holds in this
   * run (the last one it was given, unless it kept an earlier one — see
   * there), else the one `restore()` ranked first; `null` when none is known.
   * A copy: changing it does not change the monitor.
   *
   * For PERSISTING (per ERPat origin, beside `lastSeenWallMs()`). A capture is
   * stamped through {@link stamp}, which observes the clocks first and hands
   * this calibration out together with the verdict and both times.
   */
  calibration(): ClockCalibration | null {
    return this.current === null ? null : { ...this.current };
  }

  /** Policy thresholds changed (re-provisioning). Invalid values are ignored; verdicts are kept. */
  setThresholds(opts: { jumpThresholdMs?: number; uncertaintyThresholdMs?: number }): void {
    this.jumpThresholdMs = threshold(opts.jumpThresholdMs, this.jumpThresholdMs);
    this.uncertaintyThresholdMs = threshold(opts.uncertaintyThresholdMs, this.uncertaintyThresholdMs);
  }

  /**
   * The page is being hidden (visibility hidden, `pagehide`, the native pause).
   * The visible interval that ends here is checked first, so a jump made while
   * visible is never excused by hiding the page afterwards; the interval that
   * starts here counts as hidden — it may contain a sleep — even if the probe
   * still reports visible (`pagehide` and the native pause can come first).
   */
  markHidden(): void {
    this.observe();
    this.hiddenSinceAnchor = true;
  }

  /**
   * The page is visible again (visibility visible, the native resume). The
   * hidden interval that ends here is checked (a forward difference — the
   * sleep — is accepted, a backward one is not), and a strictly visible
   * interval starts, even if the probe still reports hidden (the native resume
   * can come first) — the strict choice: being wrong costs a recalibration,
   * never an excused jump.
   */
  markVisible(): void {
    this.observe();
    this.hiddenSinceAnchor = false;
  }

  /**
   * Observe both clocks now: compare the interval since the anchor, update the
   * high-water mark, re-anchor, and return the verdict. A capture uses
   * {@link stamp}, which observes the same way and also hands out the times.
   */
  check(): ClockVerdict {
    this.observe();
    return this.verdict();
  }

  /**
   * What a capture is stamped with — the ONE source of its times (plan
   * amendment C10). It observes the clocks exactly like `check()` (judges the
   * interval since the last observation, re-anchors) and then hands out,
   * together:
   *   · `verdict` — the verdict after that observation; refuse the capture
   *     when it is unsafe (readiness does: it is readiness's `clock` input);
   *   · `calibration` — a copy of the calibration the monitor HOLDS: after
   *     `calibrated()` kept a precise calibration over an imprecise one, that
   *     is the precise one, not the one the stored context carries;
   *   · `deviceNowMs` — the wall-clock reading this observation made, or the
   *     `deviceNowMs` given (the confirmation instant), rounded to a whole ms;
   *   · `correctedNowMs` — `deviceNowMs + Math.round(calibration.offsetMs)`
   *     (= `correctedNow(calibration, deviceNowMs)`), so the two times differ by
   *     exactly the `clock.offset_ms` the event carries (C1).
   *
   * Pass nothing in production: the stamp then uses the very reading the
   * observation judged. A `deviceNowMs` read earlier (before the capture lock)
   * is stamped as given; the verdict still covers the clocks up to NOW.
   *
   * `null` when nothing can be stamped: no calibration is held, or the device
   * time or the corrected time is not a finite number (a NaN must never become
   * a punch time). The observation has still been made: `verdict()` tells why.
   */
  stamp(deviceNowMs?: number): ClockStamp | null {
    const wallMs = this.now();
    this.observe(wallMs, this.perfNow());
    const held = this.current;
    if (held === null) return null;
    const device = deviceNowMs === undefined ? wallMs : deviceNowMs;
    if (!Number.isFinite(device)) return null;
    // `|| 0` turns a -0 into 0.
    const stampedMs = Math.round(device) || 0;
    // correctedNow(held, stampedMs), built from the very offset the event
    // carries (C1), so the two times differ by exactly `clock.offset_ms`.
    const correctedMs = stampedMs + Math.round(held.offsetMs);
    if (!Number.isFinite(correctedMs)) return null;
    return {
      verdict: this.verdict(),
      calibration: { ...held },
      deviceNowMs: stampedMs,
      correctedNowMs: correctedMs,
    };
  }

  /**
   * A session starts (boot, or a sign-in later in the same run).
   *
   * First the interval since the previous observation is judged like
   * `check()` — a jump made between two sessions is never re-anchored away.
   *
   * Then, unless ERPat calibrated the device earlier in this run (that
   * measurement outranks anything stored, and the previous run's records no
   * longer apply):
   *   · `calibration` — a STORED calibration — becomes the stamping
   *     calibration when the monitor holds none, or when it outranks the one
   *     held: a stampable calibration beats an unstampable one it agrees with
   *     (whichever is newer); otherwise the one ERPat vouched for more recently
   *     (a later `calibratedAtServerIso`) wins. It is adopted for the
   *     uncertainty check WITHOUT being treated as fresh: unlike `calibrated()`
   *     it clears nothing — a cached offset is not proof of trustworthy time;
   *   · the wall clock is compared with the highest wall time known from
   *     before — `lastSeenWallMs` (persisted for this ERPat origin by the
   *     previous run) and the stamping calibration's own device time. More
   *     than the threshold behind it ⇒ `regressed_since_last_run` (sticky).
   */
  restore(lastSeenWallMs: number | null, calibration?: ClockCalibration | null): ClockVerdict {
    this.observe();
    if (this.calibratedThisRun) return this.verdict();
    if (calibration !== undefined && calibration !== null && this.outranksCurrent(calibration)) {
      this.current = { ...calibration };
    }
    const priorMs = Math.max(
      finiteOr(lastSeenWallMs, Number.NEGATIVE_INFINITY),
      finiteOr(this.current?.calibratedAtDeviceMs, Number.NEGATIVE_INFINITY),
    );
    if (this.sticky === null && this.anchorWallMs < priorMs - this.jumpThresholdMs) {
      this.sticky = 'regressed_since_last_run';
    }
    this.highWaterMs = Math.max(this.highWaterMs, priorMs);
    return this.verdict();
  }

  /** The current verdict, without observing the clocks (see `check()`). */
  verdict(): ClockVerdict {
    if (this.sticky !== null) return { status: 'unsafe', reason: this.sticky };
    if (this.current !== null && !this.stampable(this.current)) {
      return { status: 'unsafe', reason: 'uncertain' };
    }
    return { status: 'safe' };
  }

  /**
   * The highest wall time seen since the last calibration — what the
   * orchestrator persists (per ERPat origin, beside `calibration()`) and hands
   * to `restore()` at the next start. Never lowered by a backward jump (so
   * restarting the app cannot clear one); reset by `calibrated()`.
   */
  lastSeenWallMs(): number {
    return this.highWaterMs;
  }

  /**
   * Judge the interval since the anchor (setting a sticky jump verdict), then
   * re-anchor at the same paired reading of both clocks.
   */
  private observe(wallMs: number = this.now(), perfMs: number = this.perfNow()): void {
    if (this.sticky === null) {
      const wallElapsed = wallMs - this.anchorWallMs;
      const monotonicElapsed = perfMs - this.anchorPerfMs;
      const limit = this.jumpThresholdMs;
      if (wallElapsed < monotonicElapsed - limit || wallMs < this.highWaterMs - limit) {
        this.sticky = 'jump_back';
      } else if (wallElapsed > monotonicElapsed + limit && !this.hiddenSinceAnchor) {
        this.sticky = 'jump_forward';
      }
    }
    this.reanchor(wallMs, perfMs);
  }

  /**
   * Whether a punch may be stamped with `cal`: an offset ERPat accepts (what
   * the event carries is `Math.round(offsetMs)`, amendment C1), an uncertainty
   * within the policy, and a server time ERPat can read back as
   * `clock.calibrated_at`.
   */
  private stampable(cal: ClockCalibration): boolean {
    return (
      Number.isFinite(cal.offsetMs) &&
      Math.abs(Math.round(cal.offsetMs)) <= OFFLINE_CLOCK_MAX_OFFSET_MS &&
      Number.isFinite(cal.uncertaintyMs) &&
      cal.uncertaintyMs >= 0 &&
      cal.uncertaintyMs <= this.uncertaintyThresholdMs &&
      serverTimeOf(cal) !== null
    );
  }

  /**
   * Whether a STORED calibration should replace the one held (`restore()`):
   *  - between a stampable and an unstampable calibration that AGREE, the
   *    stampable one wins, whichever ERPat vouched for later — the same rule
   *    `calibrated()` keeps a precise calibration by, so a restart after it
   *    kept one (FE-7 stored the imprecise one with the context) lands on the
   *    same calibration, in whatever order sessions restore;
   *  - otherwise the one ERPat vouched for later wins, and an unreadable
   *    server time never displaces a readable one.
   */
  private outranksCurrent(candidate: ClockCalibration): boolean {
    const held = this.current;
    if (held === null) return true;
    const candidateStampable = this.stampable(candidate);
    if (candidateStampable !== this.stampable(held) && agrees(candidate, held)) return candidateStampable;
    const candidateMs = serverTimeOf(candidate);
    if (candidateMs === null) return false;
    const currentMs = serverTimeOf(held);
    return currentMs === null || candidateMs > currentMs;
  }

  /**
   * Start the next interval at `(wallMs, perfMs)`. It counts as hidden only if
   * the page reports itself hidden as it starts (`markHidden()` /
   * `markVisible()` may then override that for this interval) — never because
   * an earlier `markHidden()` was not followed by `markVisible()`.
   */
  private reanchor(wallMs: number, perfMs: number): void {
    this.anchorWallMs = wallMs;
    this.anchorPerfMs = perfMs;
    this.hiddenSinceAnchor = this.probeHidden();
    if (Number.isFinite(wallMs)) this.highWaterMs = Math.max(this.highWaterMs, wallMs);
  }

  /** The visibility probe's answer; only exactly `true` is hidden. */
  private probeHidden(): boolean {
    try {
      return this.isHidden() === true;
    } catch {
      // A probe that cannot answer counts as VISIBLE: the next interval is then
      // judged strictly, so the worst case is a false clock_unsafe (cleared by
      // the next online calibration), never an excused jump — and an
      // observation (a capture's stamp) never throws because of it.
      return false;
    }
  }
}

function finiteOr(value: number | null | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
